import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Building2, Flag, GitCompareArrows, X, ShieldCheck, SlidersHorizontal, MapPin, Check, Briefcase, ExternalLink } from "lucide-react";
import { StarRating, Loading, EmptyState } from "@/components/shared";

import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Companies() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [q, setQ] = useState(params.get("q") || "");
  const [sort, setSort] = useState("rating");
  const [companies, setCompanies] = useState(null);
  const [selected, setSelected] = useState([]);
  const [licensing, setLicensing] = useState("all");
  const [remote, setRemote] = useState("all");
  const [market, setMarket] = useState(params.get("market") || "all");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [options, setOptions] = useState({ licensing: [], remote: [] });
  const [markets, setMarkets] = useState([]);

  const toggleSelect = (c) => {
    setSelected((prev) => {
      if (prev.find((x) => x.id === c.id)) return prev.filter((x) => x.id !== c.id);
      if (prev.length >= 2) return [prev[1], c];
      return [...prev, c];
    });
  };
  const isSelected = (id) => selected.some((x) => x.id === id);

  const load = (o = {}) => {
    const lic = o.licensing ?? licensing;
    const rem = o.remote ?? remote;
    const mkt = o.market ?? market;
    const ver = o.verified ?? verifiedOnly;
    setCompanies(null);
    api.get("/companies", { params: {
      search: o.search ?? q, sort: o.sort ?? sort,
      licensing: lic === "all" ? "" : lic, remote: rem === "all" ? "" : rem,
      market: mkt === "all" ? "" : mkt, verified: ver,
    } }).then(({ data }) => setCompanies(data)).catch(() => setCompanies([]));
  };

  useEffect(() => {
    load();
    api.get("/filter-options").then(({ data }) => setOptions(data)).catch(() => {});
    api.get("/markets").then(({ data }) => setMarkets(data)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const onSearch = (e) => { e.preventDefault(); setParams(q ? { q } : {}); load({ search: q }); };
  const onSort = (v) => { setSort(v); load({ sort: v }); };
  const onLicensing = (v) => { setLicensing(v); load({ licensing: v }); };
  const onRemote = (v) => { setRemote(v); load({ remote: v }); };
  const onMarket = (v) => { setMarket(v); load({ market: v }); };
  const toggleVerified = () => { const nv = !verifiedOnly; setVerifiedOnly(nv); load({ verified: nv }); };
  const clearFilters = () => { setLicensing("all"); setRemote("all"); setMarket("all"); setVerifiedOnly(false); load({ licensing: "all", remote: "all", market: "all", verified: false }); };
  const activeFilters = (licensing !== "all") + (remote !== "all") + (market !== "all") + (verifiedOnly ? 1 : 0);

  return (
    <main className={`mx-auto max-w-[1800px] px-6 py-12 sm:px-10 lg:px-16 ${selected.length > 0 ? "pb-28" : ""}`}>
      <p className="eyebrow mb-3">Discover</p>
      <h1 className="text-4xl font-bold">Browse employers</h1>
      <p className="mt-3 max-w-4xl text-slate-400">Search iGaming & sports-betting employers, read verified reviews and spot red flags before you apply.</p>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
        <form onSubmit={onSearch} className="flex flex-1 items-center gap-2 rounded-full border border-[#DDDAD1] bg-ink-900 p-1.5">
          <Search className="ml-3 h-5 w-5 text-slate-400" aria-hidden="true" />
          <input
            data-testid="company-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search companies, roles, or keywords…"
            className="min-w-0 flex-1 bg-transparent px-2 py-2 text-sm outline-none placeholder:text-slate-500"
          />
          <button data-testid="company-search-btn" className="btn-teal rounded-full text-sm">Search</button>
        </form>
        <Select value={sort} onValueChange={onSort}>
          <SelectTrigger data-testid="sort-select" className="w-full border-[#DDDAD1] bg-ink-900 sm:w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-[#DDDAD1] bg-ink-900">
            <SelectItem value="rating">Highest rated</SelectItem>
            <SelectItem value="reviews">Most reviewed</SelectItem>
            <SelectItem value="name">Name (A–Z)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Filter by signal */}
      <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="filter-bar">
        <span className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-slate-500"><SlidersHorizontal className="h-4 w-4" /> Filter</span>
        <Select value={licensing} onValueChange={onLicensing}>
          <SelectTrigger data-testid="filter-licensing" className="w-48 border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Licensing" /></SelectTrigger>
          <SelectContent className="border-[#DDDAD1] bg-ink-900">
            <SelectItem value="all">All licensing</SelectItem>
            {options.licensing.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={remote} onValueChange={onRemote}>
          <SelectTrigger data-testid="filter-remote" className="w-44 border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Remote culture" /></SelectTrigger>
          <SelectContent className="border-[#DDDAD1] bg-ink-900">
            <SelectItem value="all">All work styles</SelectItem>
            {options.remote.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={market} onValueChange={onMarket}>
          <SelectTrigger data-testid="filter-market" className="w-44 border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Hub" /></SelectTrigger>
          <SelectContent className="border-[#DDDAD1] bg-ink-900">
            <SelectItem value="all">All hubs</SelectItem>
            {markets.map((m) => <SelectItem key={m.market} value={m.market}>{m.market} ({m.count})</SelectItem>)}
          </SelectContent>
        </Select>
        <button
          data-testid="filter-verified"
          onClick={toggleVerified}
          className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors ${verifiedOnly ? "border-success-500 bg-success-500/10 text-success-500" : "border-[#DDDAD1] text-slate-300 hover:border-success-500/40"}`}
        >
          <ShieldCheck className="h-4 w-4" /> Verified reviews only
        </button>
        {activeFilters > 0 && (
          <button data-testid="filter-clear" onClick={clearFilters} className="text-sm text-slate-400 hover:text-ink">Clear ({activeFilters})</button>
        )}
      </div>

      {companies === null ? (
        <Loading label="Loading companies" />
      ) : companies.length === 0 ? (
        <div className="mt-10">
          <EmptyState icon={Building2} title="No companies found" subtitle="Try a different search term or check back soon." />
        </div>
      ) : (
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {companies.map((c, i) => (
            <div key={c.id}
              className={`card-surface lift flex flex-col p-6 animate-fade-up ${isSelected(c.id) ? "ring-2 ring-teal-500" : ""}`} style={{ animationDelay: `${i * 40}ms` }}>
              <Link to={`/company/${c.id}`} data-testid="company-card" className="flex flex-1 flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-semibold">{c.name}</h3>
                    <p className="mt-1 text-xs text-slate-500">{c.location || "-"}</p>
                  </div>
                  <span className="font-heading text-3xl font-bold text-teal-400">{c.avg_overall || "-"}</span>
                </div>
                <p className="mt-3 line-clamp-2 text-sm text-slate-400">{c.description}</p>
                <div className="mt-4 flex flex-wrap gap-2 text-xs">
                  {c.market && c.market !== "Other" && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#E7F0EE] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-teal-600"><MapPin className="h-3 w-3" />{c.market}</span>
                  )}
                  <span className="rounded-full border border-[#DDDAD1] px-2.5 py-1 text-slate-300">{c.licensing_status}</span>
                  <span className="rounded-full border border-[#DDDAD1] px-2.5 py-1 text-slate-300">{c.remote_culture}</span>
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-[#DDDAD1] pt-4">
                  <StarRating value={c.avg_overall} />
                  <span className="flex items-center gap-3 text-xs text-slate-400">
                    {c.red_flag_count > 0 && (
                      <span className="flex items-center gap-1 text-coral-500"><Flag className="h-3.5 w-3.5" /> {c.red_flag_count}</span>
                    )}
                    {c.review_count} reviews
                  </span>
                </div>
              </Link>
              {c.open_roles > 0 && c.careers_url && (
                <a
                  href={c.careers_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="company-open-roles"
                  onClick={(e) => e.stopPropagation()}
                  className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#E7F0EE] px-3 py-1.5 text-xs font-semibold text-teal-600 transition-colors hover:bg-teal-500/20"
                >
                  <Briefcase className="h-3.5 w-3.5" /> {c.open_roles} open roles
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
              <button
                onClick={() => toggleSelect(c)}
                data-testid="compare-toggle"
                className={`mt-4 flex items-center justify-center gap-2 rounded-full border py-2 text-xs font-medium transition-colors ${isSelected(c.id) ? "border-teal-400 bg-teal-500/10 text-teal-300" : "border-[#DDDAD1] text-slate-400 hover:border-teal-400/40"}`}
              >
                <span className={`grid h-4 w-4 place-items-center rounded border ${isSelected(c.id) ? "border-teal-400 bg-teal-400 text-white" : "border-[#DDDAD1]"}`}>
                  {isSelected(c.id) && <Check className="h-3 w-3" />}
                </span>
                {isSelected(c.id) ? "Selected to compare" : "Add to compare"}
              </button>
            </div>
          ))}
        </div>
      )}

      {selected.length > 0 && (
        <div data-testid="compare-bar" className="fixed inset-x-0 bottom-0 z-40 border-t border-[#DDDAD1] bg-ink-900/90 backdrop-blur-xl">
          <div className="mx-auto flex max-w-[1800px] flex-wrap items-center justify-between gap-3 px-6 py-4 sm:px-10 lg:px-16">
            <div className="flex flex-wrap items-center gap-2">
              <GitCompareArrows className="h-5 w-5 text-teal-400" aria-hidden="true" />
              <span className="text-sm text-slate-300">Comparing:</span>
              {selected.map((c) => (
                <span key={c.id} className="flex items-center gap-1.5 rounded-full border border-[#DDDAD1] bg-ink-900 px-3 py-1 text-sm">
                  {c.name}
                  <button onClick={() => toggleSelect(c)} aria-label={`Remove ${c.name}`}><X className="h-3.5 w-3.5 text-slate-400 hover:text-ink" /></button>
                </span>
              ))}
              {selected.length === 1 && <span className="text-xs text-slate-500">Pick one more…</span>}
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => setSelected([])} className="text-sm text-slate-400 hover:text-ink">Clear</button>
              <button
                data-testid="compare-go"
                disabled={selected.length < 2}
                onClick={() => nav(`/compare?a=${selected[0].id}&b=${selected[1].id}`)}
                className="btn-teal text-sm disabled:cursor-not-allowed disabled:opacity-50"
              >Compare now</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
